import { useState } from "react";
import "./EinrichtungMitMS.scss";
import { deleteToken, getFormData } from "../../services/ApiService";
import { eighteenYearsAgo, minDate, transformPayload } from "../../Constants";
import { sendToAzureFunction } from "../../services/ApiService";
import { handleFormSubmit } from "../../services/FormService";
import storePhoto from "../../resources/storePhoto.png";

interface EinrichtungMitMSProps {
  onSubmitCallback: (status: string) => void;
  tokenID: string;
  confirmedEmail: string;
  processID: number;
}

const EinrichtungMitMS: React.FC<EinrichtungMitMSProps> = ({ onSubmitCallback, tokenID, confirmedEmail, processID }) => {
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [geburtsdatum, setGeburtsdatum] = useState("");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [securePin, setSecurePin] = useState("");
  const [confirmSecurePin, setConfirmSecurePin] = useState("");
  const [sonstiges, setSonstiges] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasConsent, setHasConsent] = useState(false);

  const [vornameError, setVornameError] = useState("");
  const [nachnameError, setNachnameError] = useState("");
  const [geburtsdatumError, setGeburtsdatumError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [pinError, setPinError] = useState("");
  const [securePinError, setSecurePinError] = useState("");
  const [confirmSecurePinError, setConfirmSecurePinError] = useState("");
  const [sonstigesError, setSonstigesError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCancel = async () => {
    try {
      await deleteToken(tokenID!);
      // After deletion, reload to trigger token validation and show error message
      window.location.reload();
    } catch (error) {
      // Optionally, show an error if deletion failed
      alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
    }
  };

  // Validate phone number
  const validatePhoneNumber = (value: string) => {
    const regex = /^\+?[0-9\s\-]{9,20}$/;
    if (hasConsent && !value) {
      setPhoneNumberError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (hasConsent && !regex.test(value)) {
      setPhoneNumberError("Ungültige Telefonnummer.");
      return false;
    } else {
      setPhoneNumberError("");
      return true;
    }
  };

  // Validate Secure PIN
  const validateSecurePin = (value: string) => {
    const pinRegex = /^\d{4}$/;
    if (!value.trim()) {
      setSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!pinRegex.test(value)) {
      setSecurePinError("Bitte geben Sie eine 4-stellige PIN ein.");
      return false;
    } else if (confirmSecurePin && value !== confirmSecurePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setSecurePinError("");
      return true;
    }
  };

  // Validate Confirm Secure PIN
  const validateConfirmSecurePin = (value: string) => {
    if (!value.trim()) {
      setConfirmSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value !== securePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setConfirmSecurePinError("");
      return true;
    }
  };

  // Validate the form
  const validateForm = () => {
    let isValid = true;

    // Validate Vorname
    if (!vorname.trim()) {
      setVornameError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setVornameError("");
    }

    // Validate Nachname
    if (!nachname.trim()) {
      setNachnameError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setNachnameError("");
    }

    // Validate Geburtsdatum
    if (!geburtsdatum.trim()) {
      setGeburtsdatumError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setGeburtsdatumError("");
    }

    // Validate Email
    if (!email.trim()) {
      setEmailError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Ungültige E-Mail-Adresse.");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Validate PIN (Microsoft PIN)
    if (!pin.trim()) {
      setPinError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setPinError("");
    }

    // Validate Secure PIN
    if (!validateSecurePin(securePin)) {
      isValid = false;
    }

    // Validate Confirm Secure PIN
    if (!validateConfirmSecurePin(confirmSecurePin)) {
      isValid = false;
    }

    // Validate Sonstiges
    if (!sonstiges.trim()) {
      setSonstigesError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setSonstigesError("");
    }

    // Validate Phone Number if consent is given
    if (hasConsent && !validatePhoneNumber(phoneNumber)) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    handleFormSubmit(
      e,
      validateForm,
      getFormData,
      transformPayload,
      sendToAzureFunction,
      setIsSubmitted,
      onSubmitCallback,
      tokenID,
      confirmedEmail,
      processID
    );
  };

  return (
    <div className="email-form">
      <h2 style={{ marginTop: 0 }}>Einrichtung Notebook mit MS Account</h2>
      <img
        src={storePhoto}
        alt="Store Photo"
        style={{ width: "100%", height: "auto", maxWidth: "100%", borderRadius: "10px", marginBottom: "20px" }}
      />

      <p style={{ width: "87%", border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "5px", textAlign: "center", marginBottom: "20px" }}>
        Bitte geben Sie Ihre Daten für die Durchführung des Services ein
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Vorname</label>
          <input
            type="text"
            value={vorname}
            name="vorname"
            onChange={(e) => setVorname(e.target.value)}
            required
          />
          {isSubmitted && vornameError && (
            <small className="error-message">{vornameError}</small>
          )}
          <small>Microsoft erfragt diese Daten für Ihren Account</small>
        </div>

        <div className="form-group">
          <label>Nachname</label>
          <input
            type="text"
            value={nachname}
            name="nachname"
            onChange={(e) => setNachname(e.target.value)}
            required
          />
          {isSubmitted && nachnameError && (
            <small className="error-message">{nachnameError}</small>
          )}
        </div>

        <div className="form-group">
          <label>Geburtsdatum</label>
          <input
            min={minDate}
            max={eighteenYearsAgo} // Only allow dates at least 18 years ago
            type="date"
            value={geburtsdatum}
            name="geburtsdatum"
            onChange={(e) => setGeburtsdatum(e.target.value)}
            required
          />
          {isSubmitted && geburtsdatumError && (
            <small className="error-message">{geburtsdatumError}</small>
          )}
          <small>Microsoft erfragt diese Daten, hier geht es darum was dem Kunden angezeigt wird (Jugendschutz)</small>
        </div>

        <div className="form-group">
          <label>E-Mailadresse des Microsoft Services</label>
          <input
            type="email"
            value={email}
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {isSubmitted && emailError && (
            <small className="error-message">{emailError}</small>
          )}
          <small>Fungiert als Benutzername und Kontakt für/von Microsoft</small>
        </div>

        <div className="form-group">
          <label>PIN des Microsoft Services</label>
          <input
            type="password"
            value={pin}
            name="pin"
            onChange={(e) => setPin(e.target.value)}
            required
          />
          {isSubmitted && pinError && (
            <small className="error-message">{pinError}</small>
          )}
          <small>Wird von Microsoft verlangt bei der Einrichtung eines Laptops</small>
        </div>

        <div className="form-group">
          <label>Sonstiges / Kundenwunsch</label>
          <textarea
            value={sonstiges}
            name="sonstiges"
            onChange={(e) => setSonstiges(e.target.value)}
            rows={3}
            required
          ></textarea>
          {isSubmitted && sonstigesError && (
            <small className="error-message">{sonstigesError}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        <div className="form-group">
          <label>Neue PIN</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={securePin}
            name="SecurePIN"
            onChange={(e) => {
              setSecurePin(e.target.value);
              validateSecurePin(e.target.value);
              if (confirmSecurePin) validateConfirmSecurePin(confirmSecurePin);
            }}
            required
          />
          <small>Bitte legen Sie eine sichere 4-stellige PIN fest. Sie benötigen diese PIN für den Zugriff auf Ihre Falldetails und können von einem Mitarbeiter des Geschäfts zur Überprüfung abgefragt werden. Behandeln Sie die PIN vertraulich und geben Sie sie nicht an Dritte weiter.</small>
          {isSubmitted && securePinError && (
            <small className="error-message">{securePinError}</small>
          )}
        </div>

        <div className="form-group">
          <label>Neue PIN bestätigen</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={confirmSecurePin}
            onChange={(e) => {
              setConfirmSecurePin(e.target.value);
              validateConfirmSecurePin(e.target.value);
            }}
            required
          />
          <small>Bitte bestätigen Sie Ihre 4-stellige PIN.</small>
          {isSubmitted && confirmSecurePinError && (
            <small className="error-message">{confirmSecurePinError}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        {/* Consent Checkbox */}
        <div className="form-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={hasConsent}
              onChange={(e) => {
                setHasConsent(e.target.checked);
                if (!e.target.checked) {
                  setPhoneNumber("");
                  setPhoneNumberError("");
                }
              }}
            />
            <span className="checkmark"></span>
            Ich stimme zu, dass ich für Angelegenheiten im Zusammenhang mit dieser Anfrage kontaktiert werden darf.
          </label>
        </div>

        {hasConsent && (
          <div className="form-group">
            <label>Telefonnummer</label>
            <input
              type="tel"
              value={phoneNumber}
              name="HandyNummer"
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                validatePhoneNumber(e.target.value);
              }}
              required={hasConsent}
            />
            <small>Bitte geben Sie eine gültige Telefonnummer ein.</small>
            {isSubmitted && phoneNumberError && (
              <small className="error-message">{phoneNumberError}</small>
            )}
          </div>
        )}

        <button type="submit">Absenden</button>
        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
          Abbrechen
        </button>
        <small style={{ color: "#777" }}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
      </form>
    </div>
  );
};

export default EinrichtungMitMS;